import Phaser from 'phaser';
import DefaultButton from '../buttons/DefaultButton';
import HudButtonGroup from './HudButtonGroup';
import HudPause from './HudPause';
import { GetDefaultButtonData } from './HudConstants';
import SoundButton from '../buttons/SoundButton';
import Suitcase from '../suitcase/Suitcase';
import { STATE_MINIGAME_FOOD, MINIGAME_FOOD } from '../../constants/AppConstants';
import ScreenOverlayRotate from '../overlays/ScreenOverlayRotate';

/*

  Base hud class
  pretty much every state should create an instance of this and add it to the stage
  pass through hud id on initialization to auto populate on creation

  can also have additional buttons added to it on the fly

*/

export default class Hud extends Phaser.Group {
  constructor(game, id) {
    super(game);

    // stick to screen
    this.fixedToCamera = true;

    // allow some control outside class
    this.enabled = true;
    // keep track of all added buttons so we can enable/disable them
    this.buttons = [];
    // button groups anchor to the sides of the screen and automatically position buttons
    this.buttonGroups = [];
    this.onButtonSelect = new Phaser.Signal();
    // listen for when the game resizes
    this.game.scale.onSizeChange.add(this.handleScreenResize, this);
    // react to game pause/resume events
    this.game.onPause.add(this.handleGamePaused, this);
    this.game.onResume.add(this.handleGameResumed, this);

    // populate hud
    if (id) {
      this.populate(id);
    }
  }

  /*
  ===================================================
  POPULATE BASED ON HUD ID
  ===================================================
  */

  populate(id) {
    // grab default button requirements for screen
    const stateButtons = Hud.STATES[id];
    // store created button groups, creating them on the fly to avoid unneccessary ones
    const buttonGroups = {};
    // for each desired button
    stateButtons.forEach((btnid) => {
      // grab the button data from defaultbuttonconfig
      const btnData = GetDefaultButtonData(btnid);
      if (btnData) {
        const btn = this.createHudButton(btnData);
        btn.id = btnData.id;
        if (btn.onInputDown) {
          btn.onInputDown.add(this.handleButtonInput, this);
        }
        // store the button so we can control it when needed
        this.buttons.push(btn);
        // if the target button group does not exist - create it
        if (!buttonGroups[btnData.position]) {
          buttonGroups[btnData.position] = new HudButtonGroup(this.game, btnData.position);
          this.add(buttonGroups[btnData.position]);
        }
        // add the button to the button group, let that deal with it :)
        buttonGroups[btnData.position].addButton(btn);
      } else {
        console.log('hud : no button data for ' + btnid);
      }
    });

    // we will be looping button groups, so convert the object to and array and store
    this.buttonGroups = Object.values(buttonGroups);

    // call handleScreenResize(), this also gets called on screen resize
    /// this will make sure everything is in place :)
    this.handleScreenResize();
  }

  /*
  ===================================================
  CREATE HUD BUTTONS
  ===================================================
  */

  createHudButtonFromId(btnid) {
    // grab the button data as set below
    const btnData = GetDefaultButtonData(btnid);
    // create and return new button
    return new DefaultButton({ game: this.game, ...btnData });
  }

  createHudButton(btnData) {
    // not all buttons are equal :)
    // attach custom class to data and override here
    if (btnData.customclass) {
      switch (btnData.customclass) {
        case 'SoundButton':
          return new SoundButton({ game: this.game, id: 'sound' });
        case 'Suitcase':
          return new Suitcase(this.game);
        case 'Boom':
          return this.game.make.sprite(0, 0, 'boomerang_logo');
      }
    }

    // create and return new button
    return new DefaultButton({ game: this.game, ...btnData });
  }

  /*
  ===================================================
  BUTTON INPUT
  ===================================================
  */

  handleButtonInput(btn) {
    // bounce up button event in case parent needs it
    // not all buttons have default actions, these will be handled by the state
    // a good example would be the Suitcase / HowToPlay states :)
    this.onButtonSelect.dispatch(btn.id, btn);
    // we can handle default button actions here
    switch (btn.id) {
      case 'playgame':
      case 'pauseworldmap':
        this.game.gameController.triggerWorldState();
        this.game.paused = false;
        break;
      case 'howtoplay':
        this.game.gameController.triggerHowToPlay();
        this.game.paused = false;
        break;
      case 'home':
      case 'backtohome':
        this.game.gameController.triggerWelcome();
        this.game.paused = false;
        break;
      case 'pause':
        this.game.paused = true;
        break;
      case 'resume':
        this.game.paused = false;
        break;
      case 'pausereplay':
        this.startGame();
        break;
    }
  }

  startGame() {
    const targetGame = this.game.state.getCurrentState().key;
    const suitcaselaunched = this.game.gameController.suitcaselaunched;
    const cheatlaunched = this.game.gameController.cheatlaunched;
    if (targetGame === STATE_MINIGAME_FOOD) {
      this.game.gameController.triggerMiniGameFood({ cheatlaunched, suitcaselaunched });
    } else {
      this.game.gameController.triggerMiniGameChase({ cheatlaunched, suitcaselaunched });
    }
    this.game.paused = false;
  }

  /*
  ===================================================
  POSITIONING
  ===================================================
  */

  // updates button group positions on screen reize event
  handleScreenResize() {
    this.buttonGroups.forEach((buttonGroup) => {
      buttonGroup.alignButtons();
      buttonGroup.updatePosition();
    });

    if (!this.rotateScreen && this.game.width < this.game.height) {
      this.rotateScreen = new ScreenOverlayRotate(this.game);
      this.rotateScreen.fixedToCamera = true;
      this.game.add.existing(this.rotateScreen);
    }
    if (this.game.width > this.game.height && this.rotateScreen) {
      this.rotateScreen.destroy();
      this.rotateScreen = null;
    }
  }

  /*
  ===================================================
  ENABLE / DISABLE
  ===================================================
  */

  enable() {
    this.enabled = true;
    this.setButtonEnabled(true);
  }

  disable() {
    this.enabled = false;
    this.setButtonEnabled(false);
  }

  setButtonEnabled(value) {
    this.buttons.forEach((btn) => {
      // check if button has enable/disable
      // TODO : should actually check these are also functions.. but late :)
      if (btn.enable && btn.disable) {
        // call methods if they exist
        // custoom buttons should have these so they can handle them selves
        value ? btn.enable() : btn.disable();
      } else {
        //otherwise we assume defaultphaser schanigans
        btn.inputEnabled = value;
      }
    });
  }

  /*
  ===================================================
  PAUSE / RESUME MANAGEMENT
  ===================================================
  */

  handleGamePaused() {
    // disable all buttons, we don't want these active
    this.setButtonEnabled(false);
    if (!this.hudPause && this.game.gameController.isPlayingMiniGame()) {
      // create and add the pause screen
      this.hudPause = new HudPause(this.game);
      this.add(this.hudPause);
    }
  }

  handleGameResumed() {
    // destroy pause screen
    if (this.hudPause) {
      this.hudPause.destroy();
      this.hudPause = null;
    }
    // enable buttons if hud was enabled
    this.setButtonEnabled(this.enabled);
  }

  /*
  ===================================================
  OTHER FUNCTIONALITY
  ===================================================
  */

  addButton(button, position) {
    if (position) {
      const group = this.buttonGroups.find((group) => {
        return (group.screenAnchor = position);
      });
      if (group) {
        button.buttonGroup = group;
        buttonGroup.addButton(button);
        buttonGroup.alignButtons();
        buttonGroup.updatePosition();
      }
    } else {
      this.add(button);
    }

    this.buttons.push(button);
  }

  getButton(buttonid) {
    return this.buttons.find((btn) => {
      return btn.id === buttonid;
    });
  }

  disableButton(buttonid) {
    const btn = this.getButton(buttonid);
    if (btn) {
      if (btn.disable) {
        btn.disable();
      } else {
        btn.inputEnabled = false;
      }
    }
  }

  enableButton(buttonid) {
    const btn = this.getButton(buttonid);
    if (btn) {
      if (btn.disable) {
        btn.enable();
      } else {
        btn.inputEnabled = true;
      }
    }
  }

  setOverlayMode(value, keepbuttons) {
    this.buttons.forEach((btn) => {
      if (btn.id != 'sound' || (keepbuttons && !keepbuttons.includes(btn.id))) {
        btn.visible = value == false;
      } else {
        btn.visible = btn.visible;
      }
    });
  }

  /*
  ===================================================
  CLEAN UP
  ===================================================
  */

  destroy() {
    // clean up buttons
    this.buttons.forEach((btn) => {
      btn.destroy(true);
    });
    this.buttons = null;
    // clean up groups
    this.buttonGroups.forEach((group) => {
      group.destroy(true);
    });
    this.buttonGroups = null;
    if (this.rotateScreen) {
      this.rotateScreen.destroy();
      this.rotateScreen = null;
    }
    // clear signal
    this.onButtonSelect.removeAll();
    this.onButtonSelect = null;
    // we don't care about this anymores);
    this.game.onPause.remove(this.handleGamePaused, this);
    this.game.onResume.remove(this.handleGameResumed, this);
    this.game.scale.onSizeChange.remove(this.handleScreenResize, this);
    // super clean :)
    super.destroy(true);
  }
}

// CREATE A STATIC METHOD FOR THIS.
Hud.CreateHudButtonFromId = (btnid, game) => {
  // grab the button data as set below
  const btnData = GetDefaultButtonData(btnid);
  // create and return new button
  return new DefaultButton({ game: game, ...btnData });
};

Hud.CreateHudButton = (btnData, game) => {
  // create and return new button
  return new DefaultButton({ game, ...btnData });
};

Hud.NO_HUD = 'NO_HUD';
Hud.WELCOME = 'WELCOME';
Hud.WORLDMAP = 'WORLDMAP';
Hud.LOCATION = 'LOCATION';
Hud.MINIGAME = 'MINIGAME';
Hud.HOWTOPLAY = 'HOWTOPLAY';
Hud.PAUSE = 'PAUSE';
Hud.SUITCASE = 'SUITCASE';
Hud.CUTSCENE = 'CUTSCENE';
Hud.CUTSCENE_WATCHED = 'CUTSCENE_WATCHED';
Hud.STATES = {
  NO_HUD: [],
  WELCOME: ['boomlogo', 'howtoplay', 'sound'],
  WORLDMAP: ['suitcase', 'nextpage', 'prevpage', 'backtohome', 'sound'],
  HOWTOPLAY: ['backtohome', 'nextpage', 'prevpage', 'sound'],
  MINIGAME: ['pause'],
  PAUSE: ['pauseworldmap', 'resume', 'pausereplay', 'sound', 'home'],
  SUITCASE: ['suitcaseback', 'sound', 'nextpage', 'prevpage'],
  LOCATION: ['sound', 'nextpage', 'prevpage'],
  CUTSCENE: ['sound', 'backtohome'],
  CUTSCENE_WATCHED: ['sound', 'backtohome', 'skip'],
};



// WEBPACK FOOTER //
// ./js/app/ui/hud/Hud.js